import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { FileSpreadsheet, CheckCircle, AlertCircle, Loader2, RefreshCw, ExternalLink, Plus } from 'lucide-react';

interface GoogleSheet {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime: string;
  webViewLink: string;
}

export const GoogleSheetsOAuthIntegration = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [sheets, setSheets] = useState<GoogleSheet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingSheet, setIsCreatingSheet] = useState(false);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('google_tokens')
        .select('*')
        .eq('user_id', user.id)
        .single();

      setIsConnected(!!data);
    } catch (error) {
      console.error('Error checking Google Sheets connection:', error);
    }
  };

  const handleOAuthConnect = async () => {
    setIsConnecting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Call the enhanced OAuth integration for Google
      const { data, error } = await supabase.functions.invoke('enhanced-oauth-integration', {
        body: { action: 'initialize_oauth', provider: 'google' },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to get OAuth URL');
      }

      if (data?.clientId) {
        // Use Google OAuth with proper scopes
        const scopes = [
          'https://www.googleapis.com/auth/spreadsheets',
          'https://www.googleapis.com/auth/drive.file'
        ].join(' ');

        const redirectUri = `${window.location.origin}/data`;
        
        const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
          `client_id=${data.clientId}&` +
          `redirect_uri=${encodeURIComponent(redirectUri)}&` +
          `response_type=code&` +
          `scope=${encodeURIComponent(scopes)}&` +
          `access_type=offline&` +
          `prompt=consent`;

        window.location.href = authUrl;
      } else {
        throw new Error('No client ID received');
      }
    } catch (error: any) {
      console.error('Google Sheets OAuth error:', error);
      toast.error(error.message || 'Failed to start OAuth flow');
      setIsConnecting(false);
    }
  };

  const loadSheets = async () => {
    if (!isConnected) return;

    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase.functions.invoke('google-sheets-auth', {
        body: { action: 'list_sheets' },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      setSheets(data?.files || []);
      toast.success(`Loaded ${data?.files?.length || 0} spreadsheets from Google Drive`);
    } catch (error: any) {
      console.error('Error loading sheets:', error);
      toast.error('Failed to load Google Sheets');
    } finally {
      setIsLoading(false);
    }
  };

  const createSheet = async () => {
    setIsCreatingSheet(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      toast.info('Creating new Google Sheet...');

      const { data, error } = await supabase.functions.invoke('google-sheets-auth', {
        body: { 
          action: 'create_sheet',
          title: `NextWave Analytics - ${new Date().toISOString().split('T')[0]}`,
          data: [
            ['Date', 'Metric', 'Value', 'Category', 'Target', 'Performance'],
            [new Date().toISOString().split('T')[0], 'Revenue', '10000', 'Sales', '12000', '83%'],
            [new Date().toISOString().split('T')[0], 'Leads', '250', 'Marketing', '300', '83%'],
            [new Date().toISOString().split('T')[0], 'Conversion Rate', '2.5%', 'Analytics', '3.0%', '83%']
          ]
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      toast.success('Google Sheet created successfully!');
      await loadSheets(); // Refresh sheet list
    } catch (error: any) {
      console.error('Error creating sheet:', error);
      toast.error('Failed to create Google Sheet');
    } finally {
      setIsCreatingSheet(false);
    }
  };

  const runAIWorkflow = async () => {
    try {
      toast.info('Starting AI workflow with Google Sheets data...');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase.functions.invoke('enhanced-data-refresh', {
        body: { 
          source: 'google_sheets',
          includeAIWorkflow: true
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      toast.success('AI workflow with Google Sheets started successfully!');
    } catch (error: any) {
      console.error('Error running AI workflow:', error);
      toast.error('Failed to run AI workflow');
    }
  };

  const disconnect = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('google_tokens')
        .delete()
        .eq('user_id', user.id);

      setIsConnected(false);
      setSheets([]);
      toast.success('Disconnected from Google Sheets');
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Failed to disconnect from Google Sheets');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-green-600" />
            Google Sheets (OAuth)
            {isConnected && (
              <Badge variant="default" className="bg-green-100 text-green-800">
                <CheckCircle className="w-3 h-3 mr-1" />
                Connected
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Connect your Google account using secure OAuth to access, create, and sync Google Sheets data automatically.
            </AlertDescription>
          </Alert>

          {!isConnected ? (
            <Button 
              onClick={handleOAuthConnect} 
              disabled={isConnecting}
              className="w-full"
            >
              {isConnecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Connecting to Google...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Connect with Google OAuth
                </>
              )}
            </Button>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                <Button onClick={loadSheets} disabled={isLoading} variant="outline">
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </>
                  )}
                </Button>
                <Button 
                  onClick={createSheet} 
                  disabled={isCreatingSheet} 
                  variant="outline"
                  className="bg-green-50 hover:bg-green-100"
                >
                  {isCreatingSheet ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Sheet
                    </>
                  )}
                </Button>
                <Button onClick={runAIWorkflow} variant="outline">
                  Run AI Workflow
                </Button>
                <Button onClick={disconnect} variant="outline">
                  Disconnect
                </Button>
              </div>

              {sheets.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Your Google Sheets</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {sheets.map((sheet) => (
                      <div key={sheet.id} className="p-3 border rounded-lg">
                        <div className="font-medium">{sheet.name}</div>
                        <div className="text-sm text-muted-foreground">
                          Modified: {new Date(sheet.modifiedTime).toLocaleDateString()}
                        </div>
                        <a 
                          href={sheet.webViewLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-green-600 hover:underline flex items-center gap-1"
                        >
                          Open in Google Sheets
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {sheets.length === 0 && !isLoading && (
                <div className="text-center py-6">
                  <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">
                    No spreadsheets found. Create a Google Sheet to get started.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};